import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProvidersReviewsSection = ({ onAddToCompare }) => {
  const [addedProviders, setAddedProviders] = useState({});

  const handleAddToCompare = (provider) => {
    if (onAddToCompare) {
      const productData = {
        id: `provider-${provider?.id}`,
        type: 'Provider',
        brandName: provider?.name,
        name: provider?.specialization,
        image: provider?.logo,
        imageAlt: `${provider?.name} logo`,
        price: 'Op aanvraag',
        rating: provider?.rating,
        reviewCount: provider?.reviews,
        specs: [
          { icon: 'Award', label: 'Projecten', value: `${provider?.projects}+` },
          { icon: 'MapPin', label: 'Regio', value: provider?.region },
          { icon: 'Shield', label: 'Garantie', value: provider?.warranty }
        ],
        specialization: provider?.specialization,
        certifications: provider?.certifications,
        warranty: provider?.warranty,
        reviews: []
      };
      onAddToCompare(productData);
      setAddedProviders(prev => ({ ...prev, [provider?.id]: true }));
      setTimeout(() => {
        setAddedProviders(prev => ({ ...prev, [provider?.id]: false }));
      }, 2000);
    }
  };

  const providers = [
    {
      id: 1,
      name: 'EnergyStore Solutions',
      logo: 'https://ui-avatars.com/api/?name=ES&background=10b981&color=fff&size=80',
      rating: 4.8,
      reviews: 156,
      specialization: 'Tesla Powerwall & Sonnen specialist',
      certifications: ['Tesla Certified', 'Sonnen Partner', 'NEN 1010'],
      warranty: '15 jaar volledige garantie',
      projects: 340,
      region: 'Landelijk'
    },
    {
      id: 2,
      name: 'BatteryPro Nederland',
      logo: 'https://ui-avatars.com/api/?name=BP&background=059669&color=fff&size=80',
      rating: 4.7,
      reviews: 203,
      specialization: 'LFP batterijen & grid services',
      certifications: ['BYD Partner', 'Huawei Certified', 'KNX'],
      warranty: '12 jaar garantie + service',
      projects: 425,
      region: 'Randstad'
    },
    {
      id: 3,
      name: 'Smart Energy Storage',
      logo: 'https://ui-avatars.com/api/?name=SE&background=047857&color=fff&size=80',
      rating: 4.9,
      reviews: 187,
      specialization: 'Maatwerk oplossingen & monitoring',
      certifications: ['Victron Specialist', 'Fronius Partner'],
      warranty: '10 jaar garantie',
      projects: 298,
      region: 'Zuid-Nederland'
    }
  ];

  const customerCases = [
    {
      name: 'Familie Jansen',
      location: 'Utrecht',
      system: 'GRP Wall Battery 10 kWh (Grafeen)',
      image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=400&h=300&fit=crop',
      alt: 'Moderne gezinswoning met zonnepanelen en GRP Wall Battery thuisbatterij',
      quote: 'Sinds we de GRP Wall Battery hebben zijn we 90% zelfvoorzienend. Met 20.000 cycli gegarandeerde levensduur en de grafeen-technologie is dit echt een investering voor de lange termijn.',
      savings: '€1.100/jaar',
      selfSufficiency: '90%'
    },
    {
      name: 'Mark & Lisa',
      location: 'Eindhoven',
      system: 'GRP Wall Battery 5 kWh (LFP)',
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=400&h=300&fit=crop',
      alt: 'Energiezuinige woning met geïntegreerd GRP LFP batterijopslag systeem',
      quote: 'Met de GRP LFP-batterij en dynamische tarieven verdienen we de investering sneller terug dan verwacht. 20.000 cycli levensduur en bewezen veiligheid — echt een slimme keuze.',
      savings: '€950/jaar',
      selfSufficiency: '85%'
    },
    {
      name: 'Petra de Vries',
      location: 'Amsterdam',
      system: 'GRP Wall Battery 5 kWh (Grafeen)',
      image: 'https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=400&h=300&fit=crop',
      alt: 'Stadsappartement met compacte GRP Wall Battery thuisbatterij',
      quote: 'Perfect voor ons appartement. De GRP Wall Battery is compact, stil en we gebruiken nu bijna al onze zonne-energie zelf. Met 20.000 cycli weet ik dat dit systeem tientallen jaren meegaat.',
      savings: '€720/jaar',
      selfSufficiency: '75%'
    }
  ];

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Providers Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-4">
              Gecertificeerde batterij installateurs
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Onafhankelijk geselecteerd op basis van certificeringen, ervaring en klantbeoordelingen
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {providers?.map((provider, index) => (
              <motion.div
                key={provider?.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="bg-white rounded-xl p-6 shadow-elevation-1 hover:shadow-elevation-2 transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <Image
                    src={provider?.logo}
                    alt={`${provider?.name} logo`}
                    className="w-16 h-16 rounded-lg"
                  />
                  <div className="flex items-center space-x-1">
                    <Icon name="Star" size={16} className="text-warning fill-current" />
                    <span className="font-semibold text-foreground">{provider?.rating}</span>
                    <span className="text-sm text-muted-foreground">({provider?.reviews})</span>
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-foreground mb-2">{provider?.name}</h3>
                <p className="text-sm text-primary mb-4">{provider?.specialization}</p>

                <div className="space-y-3 mb-4">
                  <div className="flex items-center space-x-2">
                    <Icon name="Award" size={16} className="text-success" />
                    <span className="text-xs text-muted-foreground">{provider?.certifications?.join(', ')}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Shield" size={16} className="text-primary" />
                    <span className="text-xs text-muted-foreground">{provider?.warranty}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="CheckCircle" size={16} className="text-green-600" />
                    <span className="text-xs text-muted-foreground">{provider?.projects} projecten</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="MapPin" size={16} className="text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">{provider?.region}</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleAddToCompare(provider)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                      addedProviders?.[provider?.id]
                        ? 'bg-success text-white' :'bg-gray-100 hover:bg-gray-200 text-foreground'
                    }`}
                  >
                    <Icon name={addedProviders?.[provider?.id] ? 'Check' : 'GitCompare'} size={18} />
                    <span className="text-sm font-medium">
                      {addedProviders?.[provider?.id] ? 'Toegevoegd' : 'Vergelijken'}
                    </span>
                  </motion.button>
                  <Link to="/quote-request-form">
                    <Button size="lg" className="w-full sm:w-auto">
                      Offerte aanvragen
                      <Icon name="ArrowRight" size={20} className="ml-2" />
                    </Button>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <Link to="/quote-request-form">
              <Button size="lg">
                Vergelijk alle installateurs
                <Icon name="ArrowRight" size={20} className="ml-2" />
              </Button>
            </Link>
          </div>
        </motion.div>

        {/* Customer Cases */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-4">
              Ervaringen van woningeigenaren
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Echte resultaten van Nederlandse huishoudens met een thuisbatterij
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {customerCases?.map((customerCase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-xl overflow-hidden shadow-elevation-1"
              >
                <div className="relative h-48">
                  <Image
                    src={customerCase?.image}
                    alt={customerCase?.alt}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 right-4 bg-white rounded-lg px-3 py-1">
                    <p className="text-xs font-semibold text-green-600">{customerCase?.selfSufficiency} zelfvoorzienend</p>
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="flex">
                      {[...Array(5)]?.map((_, i) => (
                        <Icon key={i} name="Star" size={14} className="text-warning fill-current" />
                      ))}
                    </div>
                  </div>

                  <p className="text-sm text-muted-foreground italic mb-4">"{customerCase?.quote}"</p>

                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="font-semibold text-foreground">{customerCase?.name}</p>
                      <p className="text-xs text-muted-foreground">{customerCase?.location}</p>
                    </div>
                  </div>

                  <div className="bg-green-50 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-1">Systeem</p>
                    <p className="text-sm font-semibold text-foreground mb-2">{customerCase?.system}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">Besparing</span>
                      <span className="text-sm font-bold text-green-600">{customerCase?.savings}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProvidersReviewsSection;